import React, { useState, useEffect } from 'react';
import { Users, Shield, Layout, Plus, Edit2, Trash2, Lock, Unlock, CheckCircle, XCircle, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../../config/properties';
import './UserManagement.css';

/**
 * UserManagement Component
 * Admin interface for managing users, roles, and section visibility
 *
 * Features:
 * - User management (CRUD, lock/unlock, enable/disable)
 * - Role management (CRUD, assign to users)
 * - Section management (visibility, order, admin-only)
 */
const UserManagement = ({ onBack }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form states for creating new items
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    enabled: true
  });

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: ''
  });

  const [sectionForm, setSectionForm] = useState({
    name: '',
    displayName: '',
    description: '',
    orderPosition: 0,
    enabled: true,
    adminOnly: false
  });

  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Make API request with proper error handling
  const makeRequest = async (url, options = {}) => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Only add auth header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (response.status === 401) {
        setError('Unauthorized - Please login again');
        return null;
      }

      if (response.status === 403) {
        setError('Access denied - Admin role required');
        return null;
      }

      if (!response.ok) {
        const errorMsg = await response.text();
        const displayError = `API Error (${response.status}): ${errorMsg || response.statusText}`;
        setError(displayError);
        console.error(`API Error at ${url}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorMsg
        });
        return null;
      }

      const data = await response.json();
      return data;
    } catch (err) {
      const errorMsg = `Network Error: ${err.message}`;
      setError(errorMsg);
      console.error('Fetch error:', err);
      return null;
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching users from:', API_ENDPOINTS.users);
      const data = await makeRequest(API_ENDPOINTS.users);
      if (data !== null) {
        setUsers(Array.isArray(data) ? data : []);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching roles from:', API_ENDPOINTS.roles);
      const data = await makeRequest(API_ENDPOINTS.roles);
      if (data !== null) {
        setRoles(Array.isArray(data) ? data : []);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch sections
  const fetchSections = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching sections from:', API_ENDPOINTS.sections);
      const data = await makeRequest(API_ENDPOINTS.sections);
      if (data !== null) {
        setSections(Array.isArray(data) ? data : []);
      }
    } finally {
      setLoading(false);
    }
  };

  // Lock/Unlock user
  const toggleUserLock = async (userId, isCurrentlyUnlocked) => {
    // When isCurrentlyUnlocked is true, we want to lock (endpoint='lock')
    // When isCurrentlyUnlocked is false, we want to unlock (endpoint='unlock')
    const endpoint = isCurrentlyUnlocked ? 'lock' : 'unlock';
    const url = `${API_ENDPOINTS.users}/${userId}/${endpoint}`;
    console.log(`${endpoint} user:`, url);

    setLoading(true);
    try {
      const data = await makeRequest(url, { method: 'POST' });
      if (data !== null) {
        fetchUsers();
      }
    } finally {
      setLoading(false);
    }
  };

  // Enable/Disable user
  const toggleUserStatus = async (userId, shouldEnable) => {
    const endpoint = shouldEnable ? 'enable' : 'disable';
    const url = `${API_ENDPOINTS.users}/${userId}/${endpoint}`;
    console.log(`${endpoint} user:`, url);

    setLoading(true);
    try {
      const data = await makeRequest(url, { method: 'POST' });
      if (data !== null) {
        fetchUsers();
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    const url = `${API_ENDPOINTS.users}/${userId}`;
    console.log('Delete user:', url);

    setLoading(true);
    try {
      const data = await makeRequest(url, { method: 'DELETE' });
      if (data !== null) {
        fetchUsers();
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete role
  const deleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    const url = `${API_ENDPOINTS.roles}/${roleId}`;
    console.log('Delete role:', url);

    setLoading(true);
    try {
      const data = await makeRequest(url, { method: 'DELETE' });
      if (data !== null) {
        fetchRoles();
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete section
  const deleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    const url = `${API_ENDPOINTS.sections}/${sectionId}`;
    console.log('Delete section:', url);

    setLoading(true);
    try {
      const data = await makeRequest(url, { method: 'DELETE' });
      if (data !== null) {
        fetchSections();
      }
    } finally {
      setLoading(false);
    }
  };

  // Create or update user
  const saveUser = async (e) => {
    e.preventDefault();

    // Validation
    if (!userForm.username || !userForm.email) {
      setError('Username and email are required');
      return;
    }

    // For new users, password is required
    if (!selectedUser && !userForm.password) {
      setError('Password is required for new users');
      return;
    }

    // If password is provided, confirm password must match
    if (userForm.password && userForm.password !== userForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      let body;

      if (selectedUser) {
        // For update: use UserUpdateDto format
        body = {
          email: userForm.email,
          enabled: userForm.enabled
        };

        // Only add password if it's provided
        if (userForm.password) {
          body.newPassword = userForm.password;
        }
      } else {
        // For create: use UserCreateDto format
        body = {
          username: userForm.username,
          email: userForm.email,
          password: userForm.password,
          enabled: userForm.enabled
        };
      }

      const url = selectedUser
        ? `${API_ENDPOINTS.users}/${selectedUser.id}`
        : API_ENDPOINTS.usersCreate;

      const method = selectedUser ? 'PUT' : 'POST';

      console.log(`${method} ${url}`, body);

      const data = await makeRequest(url, {
        method,
        body: JSON.stringify(body)
      });

      if (data !== null) {
        setError(null);
        setUserForm({ username: '', email: '', password: '', confirmPassword: '', enabled: true });
        setSelectedUser(null);
        setShowForm(false);
        fetchUsers();
      }
    } finally {
      setLoading(false);
    }
  };

  // Create or update role
  const saveRole = async (e) => {
    e.preventDefault();

    if (!roleForm.name) {
      setError('Role name is required');
      return;
    }

    setLoading(true);
    try {
      const body = {
        name: roleForm.name,
        description: roleForm.description
      };

      const url = selectedRole
        ? `${API_ENDPOINTS.roles}/${selectedRole.id}`
        : API_ENDPOINTS.roles;

      const method = selectedRole ? 'PUT' : 'POST';

      console.log(`${method} ${url}`, body);

      const data = await makeRequest(url, {
        method,
        body: JSON.stringify(body)
      });

      if (data !== null) {
        setError(null);
        setRoleForm({ name: '', description: '' });
        setSelectedRole(null);
        setShowForm(false);
        fetchRoles();
      }
    } finally {
      setLoading(false);
    }
  };

  // Create or update section
  const saveSection = async (e) => {
    e.preventDefault();

    if (!sectionForm.name || !sectionForm.displayName) {
      setError('Section name and display name are required');
      return;
    }

    setLoading(true);
    try {
      const body = {
        name: sectionForm.name,
        displayName: sectionForm.displayName,
        description: sectionForm.description,
        orderPosition: parseInt(sectionForm.orderPosition),
        enabled: sectionForm.enabled,
        adminOnly: sectionForm.adminOnly
      };

      const url = selectedSection
        ? `${API_ENDPOINTS.sections}/${selectedSection.id}`
        : API_ENDPOINTS.sections;

      const method = selectedSection ? 'PUT' : 'POST';

      console.log(`${method} ${url}`, body);

      const data = await makeRequest(url, {
        method,
        body: JSON.stringify(body)
      });

      if (data !== null) {
        setError(null);
        setSectionForm({ name: '', displayName: '', description: '', orderPosition: 0, enabled: true, adminOnly: false });
        setSelectedSection(null);
        setShowForm(false);
        fetchSections();
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'roles') {
      fetchRoles();
    } else if (activeTab === 'sections') {
      fetchSections();
    }
  }, [activeTab]);

  // Filter users by search term
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="user-management-container">
      {/* Header with Back Button */}
      <div className="user-management-header">
        <div className="header-content">
          {onBack && (
            <button
              className="btn-back-header"
              onClick={onBack}
              aria-label="Back to settings"
              title="Back to settings"
            >
              ← Back
            </button>
          )}
          <Shield size={24} aria-hidden="true" />
          <div>
            <h2>{t('userManagementAndAccessControl')}</h2>
            <p>{t('manageUsersRolesAndSectionVisibility')}</p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          />
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="user-management-tabs">
        <button
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
          aria-selected={activeTab === 'users'}
          role="tab"
        >
          <Users size={18} aria-hidden="true" />
          {t('users')} ({users.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
          aria-selected={activeTab === 'roles'}
          role="tab"
        >
          <Shield size={18} aria-hidden="true" />
          {t('roles')} ({roles.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'sections' ? 'active' : ''}`}
          onClick={() => setActiveTab('sections')}
          aria-selected={activeTab === 'sections'}
          role="tab"
        >
          <Layout size={18} aria-hidden="true" />
          {t('sections')} ({sections.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="user-management-content">
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="tab-content">
            <div className="content-header">
              <div className="search-box">
                <Search size={18} aria-hidden="true" />
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('searchUsersPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label={t('searchUsersPlaceholder')}
                />
              </div>
              <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                <Plus size={18} aria-hidden="true" />
                {t('newUser')}
              </button>
            </div>

            {showForm && (
              <div className="form-card">
                <h4>{selectedUser ? `${t('editUser')}: ${selectedUser.username}` : t('createNewUserForm')}</h4>
                <form className="row g-3" onSubmit={saveUser}>
                  <div className="col-md-6">
                    <label className="form-label">{t('username')}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('username')}
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{t('email')}</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder={t('email')}
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{t('password')} {selectedUser && t('leaveEmptyToKeepCurrent')}</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder={selectedUser ? t('leaveEmptyToKeepCurrentPassword') : t('enterPassword')}
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      required={!selectedUser}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{t('confirmPassword')} {selectedUser && t('leaveEmptyIfNotChanging')}</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder={selectedUser ? t('leaveEmptyIfNotChangingPassword') : t('confirmPasswordPlaceholder')}
                      value={userForm.confirmPassword}
                      onChange={(e) => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                      required={!selectedUser}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="userEnabled"
                        checked={userForm.enabled}
                        onChange={(e) => setUserForm({ ...userForm, enabled: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="userEnabled">
                        {t('enableUser')}
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-success me-2" disabled={loading}>
                      {loading ? (selectedUser ? t('updatingUser') : t('creatingUser')) : (selectedUser ? t('saveUser') : t('createNewUser'))}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setSelectedUser(null);
                        setUserForm({ username: '', email: '', password: '', confirmPassword: '', enabled: true });
                      }}
                      disabled={loading}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div className="loading-spinner">{t('loading')}...</div>
            ) : filteredUsers.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>{t('username')}</th>
                      <th>{t('email')}</th>
                      <th>{t('status')}</th>
                      <th>{t('locked')}</th>
                      <th>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td>
                          <strong>{user.username}</strong>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          {user.enabled ? (
                            <span className="badge bg-success">{t('active')}</span>
                          ) : (
                            <span className="badge bg-danger">{t('disabled')}</span>
                          )}
                        </td>
                        <td>
                          {user.accountNonLocked ? (
                            <span className="badge bg-success">{t('unlocked')}</span>
                          ) : (
                            <span className="badge bg-warning">{t('locked')}</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setSelectedUser(user);
                                setUserForm({
                                  username: user.username,
                                  email: user.email,
                                  password: '',
                                  confirmPassword: '',
                                  enabled: user.enabled
                                });
                                setShowForm(true);
                              }}
                              aria-label="Edit user"
                              title="Edit user"
                              disabled={loading}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              className="btn btn-outline-info"
                              onClick={() => toggleUserStatus(user.id, !user.enabled)}
                              aria-label={user.enabled ? 'Disable user' : 'Enable user'}
                              title={user.enabled ? 'Disable user' : 'Enable user'}
                              disabled={loading}
                            >
                              {user.enabled ? <XCircle size={16} /> : <CheckCircle size={16} />}
                            </button>
                            <button
                              className="btn btn-outline-warning"
                              onClick={() => toggleUserLock(user.id, user.accountNonLocked)}
                              aria-label={user.accountNonLocked ? 'Lock user' : 'Unlock user'}
                              title={user.accountNonLocked ? 'Lock user' : 'Unlock user'}
                              disabled={loading}
                            >
                              {user.accountNonLocked ? <Lock size={16} /> : <Unlock size={16} />}
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => deleteUser(user.id)}
                              aria-label="Delete user"
                              title="Delete user"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <Users size={48} />
                <p>{t('noUsersFound')}</p>
              </div>
            )}
          </div>
        )}

        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('manageRoles')}</h3>
              <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                <Plus size={18} aria-hidden="true" />
                {t('newRole')}
              </button>
            </div>

            {showForm && (
              <div className="form-card">
                <h4>{selectedRole ? `${t('editRole')}: ${selectedRole.name}` : t('createNewRoleForm')}</h4>
                <form className="row g-3" onSubmit={saveRole}>
                  <div className="col-12">
                    <label className="form-label">{t('roleName')}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter role name (e.g., ROLE_ADMIN)"
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">{t('description')}</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter role description"
                      rows="3"
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-success me-2" disabled={loading}>
                      {loading ? (selectedRole ? t('updatingRole') : t('creatingRole')) : (selectedRole ? t('saveRole') : t('createNewRole'))}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setSelectedRole(null);
                        setRoleForm({ name: '', description: '' });
                      }}
                      disabled={loading}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div className="loading-spinner">{t('loading')}...</div>
            ) : roles.length > 0 ? (
              <div className="roles-grid">
                {roles.map(role => (
                  <div key={role.id} className="role-card">
                    <div className="role-header">
                      <h4>{role.name}</h4>
                      <div className="role-actions">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setSelectedRole(role);
                            setRoleForm({
                              name: role.name,
                              description: role.description
                            });
                            setShowForm(true);
                          }}
                          aria-label="Edit role"
                          disabled={loading}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteRole(role.id)}
                          aria-label="Delete role"
                          disabled={loading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="role-description">{role.description || 'No description'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Shield size={48} />
                <p>{t('noRolesFound')}</p>
              </div>
            )}
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('manageSectionVisibility')}</h3>
              <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                <Plus size={18} aria-hidden="true" />
                {t('newSection')}
              </button>
            </div>

            {showForm && (
              <div className="form-card">
                <h4>{selectedSection ? `${t('editSection')}: ${selectedSection.name}` : t('createNewSectionForm')}</h4>
                <form className="row g-3" onSubmit={saveSection}>
                  <div className="col-md-6">
                    <label className="form-label">{t('sectionName')}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter section name (e.g., FOOTBALL)"
                      value={sectionForm.name}
                      onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">{t('displayName')}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter display name (e.g., Football Stats)"
                      value={sectionForm.displayName}
                      onChange={(e) => setSectionForm({ ...sectionForm, displayName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">{t('orderPosition')}</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="0"
                      value={sectionForm.orderPosition}
                      onChange={(e) => setSectionForm({ ...sectionForm, orderPosition: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sectionEnabled"
                        checked={sectionForm.enabled}
                        onChange={(e) => setSectionForm({ ...sectionForm, enabled: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="sectionEnabled">
                        Enabled
                      </label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="sectionAdminOnly"
                        checked={sectionForm.adminOnly}
                        onChange={(e) => setSectionForm({ ...sectionForm, adminOnly: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="sectionAdminOnly">
                        {t('adminOnly')}
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">{t('description')}</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter section description"
                      rows="3"
                      value={sectionForm.description}
                      onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                    ></textarea>
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-success me-2" disabled={loading}>
                      {loading ? (selectedSection ? t('updatingSection') : t('creatingSection')) : (selectedSection ? t('saveSection') : t('createNewSection'))}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setSelectedSection(null);
                        setSectionForm({ name: '', displayName: '', description: '', orderPosition: 0, enabled: true, adminOnly: false });
                      }}
                      disabled={loading}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div className="loading-spinner">{t('loading')}...</div>
            ) : sections.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Display Name</th>
                      <th>Enabled</th>
                      <th>Admin Only</th>
                      <th>Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map(section => (
                      <tr key={section.id}>
                        <td>
                          <strong>{section.name}</strong>
                        </td>
                        <td>{section.displayName}</td>
                        <td>
                          {section.enabled ? (
                            <CheckCircle size={18} className="text-success" />
                          ) : (
                            <XCircle size={18} className="text-danger" />
                          )}
                        </td>
                        <td>
                          {section.adminOnly ? (
                            <span className="badge bg-warning">Admin Only</span>
                          ) : (
                            <span className="badge bg-info">Public</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{section.orderPosition}</span>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setSelectedSection(section);
                                setSectionForm({
                                  name: section.name,
                                  displayName: section.displayName,
                                  description: section.description,
                                  orderPosition: section.orderPosition,
                                  enabled: section.enabled,
                                  adminOnly: section.adminOnly
                                });
                                setShowForm(true);
                              }}
                              aria-label="Edit section"
                              title="Edit section"
                              disabled={loading}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => deleteSection(section.id)}
                              aria-label="Delete section"
                              title="Delete section"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <Layout size={48} />
                <p>{t('noSectionsFound')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Alert */}
      <div className="alert alert-info mt-4" role="alert">
        <strong>ℹ️ Info:</strong> This section is for administrators only. Changes made here affect user access and visibility across the entire application.
      </div>

      {/* Debug Info */}
      <div className="alert alert-secondary mt-3" role="alert" style={{ fontSize: '0.85rem' }}>
        <strong>🔧 Debug Info:</strong>
        <br />
        API Users Endpoint: <code>{API_ENDPOINTS.users}</code>
        <br />
        Auth Token: {getAuthToken() ? '✅ Present' : '❌ Missing'}
        <br />
        Users: {users.length} loaded | Roles: {roles.length} loaded | Sections: {sections.length} loaded
        <br />
        <small style={{ color: '#666' }}>
          Open browser console (F12) to see detailed API calls and errors.
        </small>
      </div>
    </div>
  );
};

export default UserManagement;
